import { type Row, type RowData } from "@tanstack/react-table";
import { Icon } from "icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "ui";

import { type RowAction, type RowActionInput } from "src/components/DataTable";
import { DataTableActionDialog } from "src/components/DataTableActionDialog";
import { DataTableActionMenuItem } from "src/components/DataTableActionMenuItem";
import { type DataTableFeatures } from "src/components/dataTableFeatures";

interface DataTableRowActionsProps<TData extends RowData> {
  row: Row<DataTableFeatures, TData>;
  rowActions: (row: Row<DataTableFeatures, TData>) => RowActionInput[];
}

export function DataTableRowActions<TData extends RowData>(
  props: DataTableRowActionsProps<TData>,
) {
  const { t } = useTranslation();
  const [dialogIndex, setDialogIndex] = useState<number | null>(null);
  const [open, setOpen] = useState(false);

  const actions = props.rowActions(props.row).filter((a): a is RowAction => {
    return !!(a && typeof a === "object");
  });

  if (actions.length === 0) return null;

  return (
    <div className="flex justify-end">
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t("dataTable.openMenu")}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <Icon name="tabler-dots" className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-max">
          {actions.map((action, index) => {
            return (
              <DataTableActionMenuItem
                key={action.id ?? index}
                action={action}
                onDialogOpen={() => {
                  return setDialogIndex(index);
                }}
                closeDropdown={() => {
                  return setOpen(false);
                }}
              />
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      {dialogIndex !== null && actions[dialogIndex] && (
        <DataTableActionDialog
          action={actions[dialogIndex]}
          open={dialogIndex !== null}
          onOpenChange={(next) => {
            if (!next) setDialogIndex(null);
          }}
        />
      )}
    </div>
  );
}
